import { useEffect, useRef } from "react";

/**
 * Modal accessibility: focus-trap + Escape-to-close + return-focus.
 *
 * Lives in .ts (imported into the .cl.jac modals) because the cl transpiler's `useRef`
 * support is unreliable and its `can with entry`/`can with exit` compile to SEPARATE
 * useEffects, so an add/removeEventListener pair split across them silently leaks the
 * listener. A single real-TS useEffect with a cleanup return is the correct home; the
 * .cl.jac side only receives back a plain ref and passes it to `ref={}`.
 *
 * Usage (in a .cl.jac modal):
 *   import from "./modalA11y.ts" { useModalA11y }
 *   dialogRef = useModalA11y(lambda { if onClose { onClose(); } });
 *   <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="...">
 */
const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useModalA11y(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  // Hold the latest onClose in a ref so the effect below runs ONCE on mount, not on
  // every render. Callers pass a fresh lambda each render (`useModalA11y(lambda {...})`),
  // so a `[onClose]` dep would re-run the effect every render and keep stealing focus
  // back to the first control - which broke typing into the ShareDialog invite input.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const prevFocused = document.activeElement as HTMLElement | null;
    const el = ref.current;
    // focus the first focusable control so keyboard users land inside the dialog
    el?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onCloseRef.current(); return; }
      if (e.key !== "Tab" || !el) return;
      const nodes = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (nodes.length === 0) return;
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      prevFocused?.focus?.();     // return focus to whatever opened the modal
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}
